import { useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMenu } from 'global/MenuContext'; // Ajuste se o menu completo estiver em outro contexto
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import { useTheme } from 'hooks/useTheme';
import { useMeetingRooms } from 'pages/Rooms/hooks/useMeetingRooms'; // ajuste o path se necessário
import { MenuItem } from 'types/menuTypes';

// useIframeCommunication.tsx

interface IframeMessage {
  type: string;
  roomId?: string;
  eventId?: string;
  payload?: any;
}

interface MenuItemWithParents {
  item: MenuItem;
  parents: MenuItem[];
}

const findMenuItemAndParents = (menu: MenuItem[], targetId: string): MenuItemWithParents | null => {
  const findInSubMenu = (
    items: MenuItem[],
    targetId: string,
    parents: MenuItem[] = []
  ): MenuItemWithParents | null => {
    for (const item of items) {
      if (item.documentId === targetId) {
        return { item, parents };
      }
      if (item.subMenu) {
        const result = findInSubMenu(item.subMenu, targetId, [...parents, item]);
        if (result) return result;
      }
    }
    return null;
  };

  return findInSubMenu(menu, targetId);
};

// Busca a sala pelo internal_id
function findRoomByInternalId(collections: any[], internalId: string) {
  for (const collection of collections) {
    for (const room of collection.rooms) {
      if (String(room.internal_id) === String(internalId)) return room;
    }
  }
  return null;
}

export const useIframeCommunication = (iframeUrl: string) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const { menu } = useMenu();
  const menuRoutes = useMenuRoutes();

  const { collections } = useMeetingRooms();
  const { selectMenuByDocumentId, selectMenuByPath } = useMenuSelection();
  const { updateMenuTitle } = useMenuTitle();
  const { theme } = useTheme();

  const [iframeReady, setIframeReady] = useState(false);
  const [lastHandledDocumentId, setLastHandledDocumentId] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'failed'>(
    'connecting'
  );
  const [rescheduleMeetingPayload, setRescheduleMeetingPayload] = useState<any>(null);
  const [accessRoomPayload, setAccessRoomPayload] = useState<any>(null);

  const sendMessageToIframe = (message: IframeMessage) => {
    if (iframeRef.current?.contentWindow && iframeReady) {
      try {
        iframeRef.current.contentWindow.postMessage(message, '*');
        console.log('[IFRAME] Mensagem enviada:', message);
      } catch (error) {
        console.error('[IFRAME] Erro ao enviar mensagem:', error);
      }
    } else {
      console.warn('[IFRAME] Iframe não está pronto para receber mensagens');
    }
  };

  // Envia tema para o iframe
  const sendThemeToIframe = () => {
    if (theme && iframeReady) {
      sendMessageToIframe({
        type: 'THEME_UPDATE',
        payload: theme,
      });
    }
  };

  // Envia estado de navegação para o iframe
  const sendNavigationState = () => {
    if (iframeReady) {
      sendMessageToIframe({
        type: 'NAVIGATION_STATE',
        payload: {
          currentPath: window.location.pathname,
          menu: menu,
        },
      });
    }
  };

  // Handler para quando o iframe carrega
  const handleIframeLoad = () => {
    console.log('[IFRAME] Iframe carregado, estabelecendo comunicação...');
    setIframeReady(true);
    setConnectionStatus('connected');

    // Envia dados iniciais
    setTimeout(() => {
      sendThemeToIframe();
      sendNavigationState();
    }, 100);
  };

  // Handler para quando o iframe falha
  const handleIframeError = () => {
    console.error('[IFRAME] Erro ao carregar iframe');
    setConnectionStatus('failed');
  };

  console.log('[IFRAME] menu:', menu);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verifica origem da mensagem
      if (event.origin !== new URL(iframeUrl).origin) {
        console.warn('[IFRAME] Mensagem de origem desconhecida:', event.origin);
        return;
      }

      const { type, payload } = event.data;

      console.log('[IFRAME] Mensagem recebida:', { type, payload });

      switch (type) {
        case 'IFRAME_READY':
          console.log('[IFRAME] Iframe reportou que está pronto');
          setIframeReady(true);
          setConnectionStatus('connected');
          // Envia dados iniciais
          sendThemeToIframe();
          sendNavigationState();
          break;

        case 'NAVIGATE':
          if (payload?.path) {
            console.log('[IFRAME] Navegando para:', payload.path);
            selectMenuByPath(payload.path);
            navigate(payload.path);
          }
          break;

        case 'SELECT_MENU':
          if (payload?.documentId) {
            console.log('[IFRAME] Selecionando menu:', payload.documentId);
            selectMenuByDocumentId(payload.documentId, true);
          }
          break;

        case 'ROOM_JOINED':
          const room = collections
            ?.find(collection => collection.rooms.some(r => r.id?.toString() === payload.roomId))
            ?.rooms.find(r => r.id?.toString() === payload.roomId);

          if (!room) {
            console.error('[IFRAME] Sala não encontrada:', payload.roomId);
            return;
          }

          console.log('[IFRAME][DEBUG] room encontrado:', room);
          const result = findMenuItemAndParents(menu, String(room.id));

          if (!result) {
            console.error('[IFRAME] Menu da sala não encontrado!');
            return;
          }

          const { item: menuItem, parents } = result;

          if (menuItem.level === 'third') {
            selectMenuByPath(menuItem.path ?? null);
          } else if (menuItem.level === 'second') {
            selectMenuByPath(menuItem.path ?? null);
          } else if (menuItem.level === 'first') {
            selectMenuByPath(menuItem.path ?? null);
          }

          navigate(menuItem.path);
          break;

        case 'THEME_REQUEST':
          // Iframe solicitou o tema atual
          sendThemeToIframe();
          break;

        case 'NAVIGATION_REQUEST':
          // Iframe solicitou o estado de navegação
          sendNavigationState();
          break;

        case 'ERROR':
          console.error('[IFRAME] Erro reportado pelo iframe:', payload);
          break;

        case 'VIEW_PROFILE':
          console.log('[IFRAME] Visualizando perfil:', payload);
          const route = menuRoutes.find((r: any) => r.documentId === 'c7jp5768k53sra3u6bv5ynxg');
          if (route) {
            const menuContext = {
              documentId: route.documentId,
              path: route.path,
              preserveLevel: true,
            };

            if (route.documentId && route.path) {
              selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
            }
            navigate(route.path, {
              state: {
                selectedUser: payload.uuid,
                menuContext,
              },
            });
          } else {
            console.warn('Rota não encontrada para o documentId');
          }
          break;

        case 'RESCHEDULE_MEETING':
          console.log('[IFRAME] Reagendando reunião:', payload);
          setRescheduleMeetingPayload(payload);
          break;

        case 'ACCESS_ROOM':
          console.log('[IFRAME] Acessando sala:', payload);
          setAccessRoomPayload(payload);
          break;

        default:
          console.warn('[IFRAME] Tipo de mensagem desconhecido:', type);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    menu,
    collections,
    navigate,
    selectMenuByDocumentId,
    selectMenuByPath,
    iframeUrl,
    iframeReady,
  ]);

  // Envia tema sempre que mudar
  useEffect(() => {
    if (theme && iframeReady) {
      sendThemeToIframe();
    }
  }, [theme, iframeReady]);

  // Envia estado de navegação sempre que mudar
  useEffect(() => {
    if (iframeReady) {
      sendNavigationState();
    }
  }, [menu, iframeReady]);

  // Ping para verificar se iframe ainda está responsivo
  useEffect(() => {
    if (!iframeReady) return;

    const pingInterval = setInterval(() => {
      sendMessageToIframe({
        type: 'PING',
        payload: { timestamp: Date.now() },
      });
    }, 30000); // Ping a cada 30 segundos

    return () => clearInterval(pingInterval);
  }, [iframeReady]);

  return {
    iframeRef,
    sendMessageToIframe,
    iframeReady,
    connectionStatus,
    handleIframeLoad,
    handleIframeError,
    rescheduleMeetingPayload,
    accessRoomPayload,
    setAccessRoomPayload,
  };
};
